import React, { useRef, useState } from 'react';

import { MimeType } from '@aline/common';
import { DialogContent } from '@mui/material';
import {
  AlineDialog,
  AlineDialogButtons,
  ConfirmationDialog,
  DocumentPicker,
  DocumentPickerVariant,
} from 'src/components';
import type { DocumentPickerRow } from 'src/components';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useApplySignedVersionOutsideAline } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  buttons: {
    flexShrink: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '75vh',
    maxHeight: 'calc(100vh - 64px)',
    maxWidth: 1080,
    minWidth: '75%',
  },
  content: {
    boxSizing: 'border-box',
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    minHeight: 0,
    width: '100%',
  },
}));

interface SignOutsideAlineRepositoryDialogProps {
  onClose: () => void;
  open: boolean;
  targetDocument: Pick<Apollo.Document, 'id' | 'name'>;
  workflowId: UUID;
}

/**
 * Pick a standalone repository PDF to become the signed version of a workflow
 * document. Confirming copies the PDF onto the target and permanently deletes the source.
 */
export function SignOutsideAlineRepositoryDialog({
  onClose,
  open,
  targetDocument,
  workflowId,
}: SignOutsideAlineRepositoryDialogProps): JSX.Element | null {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const namesByIdRef = useRef(new Map<UUID, string>());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { applySignedVersionOutsideAline, loading: submitting } =
    useApplySignedVersionOutsideAline();
  const [selectedDocumentId, setSelectedDocumentId] = useState<UUID | null>(null);
  const selectedDocumentName = selectedDocumentId
    ? (namesByIdRef.current.get(selectedDocumentId) ?? 'This document')
    : 'This document';

  function handleClose(): void {
    setConfirmOpen(false);
    setSelectedDocumentId(null);
    namesByIdRef.current.clear();
    onClose();
  }

  function handlePickerDone(): void {
    if (!selectedDocumentId) return;
    setConfirmOpen(true);
  }

  async function handleConfirm(): Promise<void> {
    if (!selectedDocumentId) return;

    await applySignedVersionOutsideAline(targetDocument.id, {
      variables: {
        input: {
          documentId: targetDocument.id,
          sourceDocumentId: selectedDocumentId,
          workflowId,
        },
      },
    });
    handleClose();
  }

  if (!currentUser?.myRepositoryId) return null;

  return (
    <AlineDialog
      classes={{ paperWidthMd: classes.container }}
      contentPaddingBottomPx={24}
      contentPaddingTopPx={8}
      disableAltClose={submitting}
      loading={submitting}
      open={open}
      title="Choose from Aline repository"
      onClose={handleClose}
    >
      <DialogContent className={classes.content}>
        <DocumentPicker
          showFiltersButton
          documentVariant={Apollo.DocumentVariant.Document}
          getIsRowDisabled={row => {
            namesByIdRef.current.set(row.id, row.name);
            return getRepositoryPickDisabledReason(row, targetDocument.id);
          }}
          pickerVariant={DocumentPickerVariant.SingleSelect}
          repositoryId={currentUser.myRepositoryId}
          searchPlaceholder="Search documents..."
          onSelectionChange={ids => setSelectedDocumentId(ids.at(0) ?? null)}
        />
      </DialogContent>
      <AlineDialogButtons
        cancelHandler={handleClose}
        className={classes.buttons}
        disabled={!selectedDocumentId || confirmOpen}
        submitHandler={handlePickerDone}
        submitText="Continue"
      />
      <ConfirmationDialog
        disableAltClose={submitting}
        message={`A copy of "${selectedDocumentName}" will be added as the latest signed version of "${targetDocument.name}", then the original will be permanently deleted. This cannot be undone.`}
        open={confirmOpen}
        primaryText="Continue"
        title="Use this document as the signed version?"
        onClickPrimary={handleConfirm}
        onClose={() => setConfirmOpen(false)}
      />
    </AlineDialog>
  );
}

function getRepositoryPickDisabledReason(
  row: DocumentPickerRow,
  targetDocumentId: UUID
): { isRowDisabled: boolean; reason: string } {
  if (row.id === targetDocumentId)
    return { isRowDisabled: true, reason: 'Cannot use this workflow document as its own source.' };

  if (row.workflow)
    return {
      isRowDisabled: true,
      reason: 'Choose a document that is not attached to a workflow.',
    };

  if (row.latestVersion.__typename !== 'StaticFileVersion')
    return { isRowDisabled: true, reason: 'Only PDF documents can be used as a signed version.' };

  if (row.latestVersion.fileLocation.mimetype !== MimeType.PDF)
    return { isRowDisabled: true, reason: 'Only PDF documents can be used as a signed version.' };

  return { isRowDisabled: false, reason: '' };
}
